/**
 * One operational status vocabulary for the client desk.
 * Words carry meaning; color only supports them.
 */

export const DESK_STATUS = {
  live: 'Live',
  /** r17: feed/transport health never says LIVE (LIVE = trading mode only). */
  streaming: 'Streaming',
  paper: 'Paper',
  delayed: 'Delayed',
  pending: 'Pending',
  unavailable: 'Unavailable',
  updated: 'Updated',
  rtError: 'RT Error',
  rtOff: 'RT Off',
  rtConnecting: 'RT Connecting',
  connecting: 'Connecting',
  pathError: 'Path Error',
  offline: 'Offline',
  ageUnknown: 'Age unknown',
  loading: 'Loading',
  empty: 'Empty',
} as const

export type DeskStatusLabel = (typeof DESK_STATUS)[keyof typeof DESK_STATUS]
export type DeskStatusTone = 'ok' | 'warn' | 'error' | 'neutral' | 'pending'
export type DeskPathState = 'live' | 'connecting' | 'error' | 'offline'
export type DeskRealtimeState = 'on' | 'connecting' | 'error' | 'off'
export type QuoteAge = 'fresh' | 'aging' | 'stale' | 'unknown'

export interface DeskStatusMark {
  kind: 'mode' | 'feed' | 'rt' | 'market'
  label: string
  tone: DeskStatusTone
  title: string
  silent?: boolean
}

export function deskModeMark(
  environment?: string | null
): DeskStatusMark | null {
  const env = String(environment || '').trim().toLowerCase()
  if (env === 'paper' || env === 'simulated') {
    return {
      kind: 'mode',
      label: DESK_STATUS.paper,
      tone: 'warn',
      title: 'Paper trading account',
    }
  }
  if (env === 'live' || env === 'production' || env === 'real') {
    return {
      kind: 'mode',
      label: DESK_STATUS.live,
      tone: 'ok',
      title: 'Live trading account',
    }
  }
  return null
}

export function deskFeedMark(path: DeskPathState): DeskStatusMark {
  switch (path) {
    case 'live':
      return {
        kind: 'feed',
        label: DESK_STATUS.streaming,
        tone: 'ok',
        title: 'Market data path is streaming',
      }
    case 'connecting':
      return {
        kind: 'feed',
        label: DESK_STATUS.connecting,
        tone: 'warn',
        title: 'Connecting to trading services',
      }
    case 'error':
      return {
        kind: 'feed',
        label: DESK_STATUS.pathError,
        tone: 'error',
        title: 'Data path error',
      }
    default:
      return {
        kind: 'feed',
        label: DESK_STATUS.offline,
        tone: 'warn',
        title: 'Data path offline',
      }
  }
}

export function deskRealtimeMark(realtime: DeskRealtimeState): DeskStatusMark {
  switch (realtime) {
    case 'on':
      return {
        kind: 'rt',
        label: DESK_STATUS.streaming,
        tone: 'ok',
        title: 'Realtime transport is streaming',
        silent: true,
      }
    case 'connecting':
      return {
        kind: 'rt',
        label: DESK_STATUS.rtConnecting,
        tone: 'warn',
        title: 'Realtime transport is connecting',
      }
    case 'error':
      return {
        kind: 'rt',
        label: DESK_STATUS.rtError,
        tone: 'error',
        title: 'Realtime transport error',
      }
    default:
      return {
        kind: 'rt',
        label: DESK_STATUS.rtOff,
        tone: 'warn',
        title: 'Realtime transport is off',
      }
  }
}

export function quoteAgeState(
  value: string | null | undefined,
  now: number
): QuoteAge {
  if (!value) return 'unknown'
  const time = Date.parse(value)
  if (!Number.isFinite(time)) return 'unknown'
  const minutes = (now - time) / 60_000
  if (minutes < 0) return 'unknown'
  if (minutes < 5) return 'fresh'
  if (minutes < 60) return 'aging'
  return 'stale'
}

export function quoteFreshnessLabel(age: QuoteAge): DeskStatusLabel {
  if (age === 'fresh') return DESK_STATUS.updated
  if (age === 'unknown') return DESK_STATUS.ageUnknown
  return DESK_STATUS.delayed
}

export function coalesceMarketFeed(states: readonly string[]): string {
  if (states.length === 0) return DESK_STATUS.empty
  const unique = [...new Set(states)]
  if (unique.length === 1) return unique[0] ?? DESK_STATUS.empty
  if (unique.includes(DESK_STATUS.delayed)) return DESK_STATUS.delayed
  if (unique.includes(DESK_STATUS.ageUnknown)) return DESK_STATUS.ageUnknown
  if (unique.includes(DESK_STATUS.unavailable)) return DESK_STATUS.unavailable
  if (unique.includes(DESK_STATUS.updated)) return DESK_STATUS.updated
  return unique[0] ?? DESK_STATUS.empty
}

const PENDING_ORDER = new Set([
  'accepted',
  'cancel_pending',
  'cancel_rejected',
  'cancel_requested',
  'new',
  'open',
  'partial',
  'partially_filled',
  'pending',
  'pending_cancel',
  'submitted',
  'working',
])

export function blotterStatusLabel(raw: string | null | undefined): string {
  const value = String(raw ?? '').trim()
  if (!value) return DESK_STATUS.pending
  const key = value.toLowerCase().replace(/\s+/g, '_')
  if (PENDING_ORDER.has(key)) return DESK_STATUS.pending
  if (key === 'filled' || key === 'complete' || key === 'completed') return 'Filled'
  if (key === 'cancelled' || key === 'canceled') return 'Cancelled'
  if (key === 'expired') return 'Expired'
  if (key === 'rejected') return 'Rejected'
  if (key === 'unavailable') return DESK_STATUS.unavailable
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function deskStatusToneClass(tone: DeskStatusTone): DeskStatusTone {
  return tone
}
